import { TComponentType } from '../../AppPage2/types'
import { BaseUISchema } from '../common-types'
import { DataTableV2Column } from '../DataTableV2/types'
import { JsonFormSchemaV2 } from '../FormComponent/types'

// --- Response ---

export interface StatementEditorV2Response {
  uiSchema: StatementEditorV2UISchema
  dataSchema: StatementEditorV2DataSchema
  filters: JsonFormSchemaV2 | Record<string, never>
}

// --- Action types ---

export interface StatementEditorV2BaseAction {
  id: string
  label: string
  icon?: string
  isVisible: boolean
  isDisabled: boolean
}

export interface StatementEditorV2SheetContentAction extends StatementEditorV2BaseAction {
  type: 'sheet-content'
  content: StatementEditorV2SheetContentActionContent
}

export interface StatementEditorV2SheetContentActionContent {
  contentId: string
  contentType: TComponentType
}

export interface StatementEditorV2SubmissionAction extends StatementEditorV2BaseAction {
  type: 'submit'
  handlerId: string
  requireConfirmation: boolean
  confirmationMessage?: string
}

export type StatementEditorV2Action = StatementEditorV2SheetContentAction | StatementEditorV2SubmissionAction

// --- UI Schema ---

export interface StatementEditorV2UISchema extends BaseUISchema {
  reOrderHandlerId?: string
  /** Data-form content handler for creating content blocks from index edit mode. */
  addContentFormContentId?: string
  columns: number
  fields: StatementEditorV2FieldDefinition[]
  actions?: StatementEditorV2Action[]
  /** Actions rendered in the component header next to tile filters. */
  headerActions?: StatementEditorV2Action[]
}

// --- Field definition (flattened, display-only) ---

export interface StatementEditorV2FieldDefinition {
  propertyId: string
  title: string
  description: string
  layout: StatementEditorV2LayoutConfig
  fields?: StatementEditorV2FieldDefinition[]
  /** Actions rendered in each nested item header (e.g. edit statement). */
  actions?: StatementEditorV2Action[]
  /** Actions rendered in the field column title row (e.g. add statement). */
  headerActions?: StatementEditorV2Action[]
}

export interface StatementEditorV2LayoutConfig {
  position: 'columns' | 'header' | 'content'
  dataDisplay?: DataTableV2Column['dataDisplay']
  columnLayout?: StatementEditorV2ColumnLayout
}

export interface StatementEditorV2ColumnLayout {
  colStart: number
  colSpan: number
}

// --- Data Schema ---

export interface StatementEditorV2DataSchema {
  indexData: StatementEditorV2IndexNode[]
  contentData: StatementEditorV2ContentDataType[]
}

export interface StatementEditorV2BaseNode {
  id: string
  /**
   * @deprecated Unused by the UI — depth is inferred from nesting. Legacy payloads may still include this field.
   */
  type?: string
  title: string
  /** Optional metadata — not rendered by the index tree UI today. */
  description?: string
}

export interface StatementEditorV2IndexNode extends StatementEditorV2BaseNode {
  children?: StatementEditorV2IndexNode[]
  contentRefs?: string[]
}

/** @deprecated Use StatementEditorV2IndexNode instead. */
export type StatementEditorV2ChapterNode = StatementEditorV2IndexNode
/** @deprecated Use StatementEditorV2IndexNode instead. */
export type StatementEditorV2SectionNode = StatementEditorV2IndexNode
/** @deprecated Use StatementEditorV2IndexNode instead. */
export type StatementEditorV2TopicNode = StatementEditorV2IndexNode

export type StatementEditorV2ContentDataType = {
  id: string
} & Record<string, unknown>

export interface StatementEditorV2ContentDataTypeWithPosition extends StatementEditorV2ContentDataType {
  position: string[]
}

// --- Type guards ---

export function isStatementEditorV2UISchema(
  value: StatementEditorV2UISchema | Record<string, never>
): value is StatementEditorV2UISchema {
  return (
    typeof value === 'object' &&
    value !== null &&
    'columns' in value &&
    'fields' in value &&
    Array.isArray((value as StatementEditorV2UISchema).fields)
  )
}

export function isStatementEditorV2DataSchema(
  value: StatementEditorV2DataSchema | Record<string, never>
): value is StatementEditorV2DataSchema {
  return typeof value === 'object' && value !== null && 'indexData' in value && 'contentData' in value
}
